import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';

/**
 * Voice Enrollment Component for AURA Speaker Verification
 * 
 * Features:
 * - View enrollment status
 * - Record voice samples for enrollment
 * - Manage verification settings
 * - Unenroll (delete voiceprint)
 */

const ENROLLMENT_PHRASES = [
  "Hello AURA, this is my voice",
  "My name is the account owner, verify my identity",
  "AURA, I authorize this voice for trading",
  "This voice belongs to the owner of this account",
  "AURA, remember my voice for verification"
];

const VoiceEnrollment = () => {
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    verification_threshold: 0.75,
    require_verification_for_trades: true,
    require_verification_for_auto_trade: true,
    require_verification_for_settings: true
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Audio recording refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Fetch enrollment status on mount
  useEffect(() => {
    fetchEnrollmentStatus();
  }, []);

  const fetchEnrollmentStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/v1/robot/voice-verification/status');
      if (response.data.success) {
        setEnrollmentStatus(response.data.data);
        // Update settings from server
        if (response.data.data) {
          setSettings({
            verification_threshold: response.data.data.verification_threshold || 0.75,
            require_verification_for_trades: response.data.data.require_verification_for_trades ?? true,
            require_verification_for_auto_trade: response.data.data.require_verification_for_auto_trade ?? true,
            require_verification_for_settings: response.data.data.require_verification_for_settings ?? true
          });
        }
      }
    } catch (err) {
      console.error('Error fetching enrollment status:', err);
      setError('Failed to load voice enrollment status');
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        }
      });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Convert to base64
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          const base64Audio = reader.result.split(',')[1];
          setRecordings(prev => [...prev, {
            index: currentPhraseIndex,
            phrase: ENROLLMENT_PHRASES[currentPhraseIndex],
            audio: base64Audio,
            blob: audioBlob
          }]);
          setCurrentPhraseIndex(prev => Math.min(prev + 1, ENROLLMENT_PHRASES.length - 1));
        };
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      // Auto-stop after 5 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      }, 5000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Failed to access microphone. Please grant microphone permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  const removeRecording = (index) => {
    setRecordings(prev => prev.filter((_, i) => i !== index));
  };

  const enrollVoice = async () => {
    if (recordings.length < 3) {
      setError('Please record at least 3 voice samples');
      return;
    }
    
    setEnrolling(true);
    setError(null);
    setSuccess(null);
    
    try {
      const samples = recordings.map(r => r.audio);
      
      const response = await api.post('/api/v1/robot/voice-verification/enroll', {
        samples
      });
      
      if (response.data.success) {
        setSuccess(`Voice enrollment successful! Quality score: ${(response.data.quality_score * 100).toFixed(0)}%`);
        setRecordings([]);
        setCurrentPhraseIndex(0);
        fetchEnrollmentStatus();
      }
    } catch (err) {
      console.error('Error enrolling voice:', err);
      setError(err.response?.data?.detail || 'Failed to enroll voice');
    } finally {
      setEnrolling(false);
    }
  };

  const unenrollVoice = async () => {
    if (!window.confirm('Are you sure you want to delete your voiceprint? This will disable voice verification.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.delete('/api/v1/robot/voice-verification/unenroll');
      if (response.data.success) {
        setSuccess('Voiceprint deleted successfully');
        fetchEnrollmentStatus();
      }
    } catch (err) {
      console.error('Error unenrolling:', err);
      setError(err.response?.data?.detail || 'Failed to delete voiceprint');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setError(null);
    
    try {
      const response = await api.put('/api/v1/robot/voice-verification/settings', settings);
      if (response.data.success) {
        setSuccess('Settings saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const playRecording = (blob) => {
    const audio = new Audio(URL.createObjectURL(blob));
    audio.play();
  };

  if (loading && !enrollmentStatus) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading voice verification status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          Voice Verification for AURA
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Enroll your voice to enable speaker verification for sensitive AURA commands like trading and auto-trade.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {/* Enrollment Status Card */}
      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Enrollment Status</h3>
            <div className="flex items-center mt-2">
              {enrollmentStatus?.is_enrolled ? (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-green-700 font-medium">Voice Enrolled</span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                  <span className="text-yellow-700 font-medium">Not Enrolled</span>
                </>
              )}
            </div>
          </div>
          {enrollmentStatus?.is_enrolled && (
            <div className="text-right text-sm text-gray-600">
              <p>Samples: {enrollmentStatus.enrollment_samples_count}</p>
              <p>Enrolled: {enrollmentStatus.enrollment_completed_at ? new Date(enrollmentStatus.enrollment_completed_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          )}
        </div>
        
        {/* Verification Stats */}
        {enrollmentStatus?.is_enrolled && enrollmentStatus.verification_stats && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Verification Statistics</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{enrollmentStatus.verification_stats.total_attempts}</div>
                <div className="text-xs text-gray-500">Total Attempts</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{enrollmentStatus.verification_stats.successful}</div>
                <div className="text-xs text-gray-500">Successful</div>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{enrollmentStatus.verification_stats.failed}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Voice Enrollment Section */}
      {!enrollmentStatus?.is_enrolled && (
        <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-4">Enroll Your Voice</h3>
          <p className="text-sm text-blue-800 mb-4">
            Record at least 3 voice samples (5 seconds each) to create your voiceprint. 
            Speak clearly in a quiet environment.
          </p>
          
          {/* Current Phrase */}
          <div className="bg-white p-4 rounded-lg mb-4">
            <p className="text-xs text-gray-500 mb-1">Phrase {Math.min(recordings.length + 1, ENROLLMENT_PHRASES.length)} of {ENROLLMENT_PHRASES.length}</p>
            <p className="text-lg font-medium text-gray-800">"{ENROLLMENT_PHRASES[currentPhraseIndex]}"</p>
          </div>
          
          {/* Recording Button */}
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={recordings.length >= ENROLLMENT_PHRASES.length}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                isRecording 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : recordings.length >= ENROLLMENT_PHRASES.length
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isRecording ? (
                <>
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  Stop Recording
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  Record Sample
                </>
              )}
            </button>
            <span className="text-sm text-gray-600">
              {recordings.length} / 3 samples recorded (minimum)
            </span>
          </div>
          
          {/* Recorded Samples */}
          {recordings.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Recorded Samples:</h4>
              {recordings.map((rec, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                  <span className="text-sm text-gray-700">Sample {idx + 1}: "{rec.phrase}"</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => playRecording(rec.blob)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Play"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => removeRecording(idx)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Enroll Button */}
          <button
            onClick={enrollVoice}
            disabled={recordings.length < 3 || enrolling}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              recordings.length < 3 || enrolling
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {enrolling ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Processing Voice Samples...
              </div>
            ) : (
              'Complete Enrollment'
            )}
          </button>
        </div>
      )}

      {/* Verification Settings */}
      {enrollmentStatus?.is_enrolled && (
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-4">Verification Settings</h3>
          
          <div className="space-y-4">
            {/* Threshold Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Sensitivity: {(settings.verification_threshold * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="50"
                max="95"
                value={settings.verification_threshold * 100}
                onChange={(e) => setSettings(prev => ({ ...prev, verification_threshold: e.target.value / 100 }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>More Lenient</span>
                <span>More Strict</span>
              </div>
            </div>
            
            {/* Toggle Options */}
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">Require verification for trading commands</span>
                <input
                  type="checkbox"
                  checked={settings.require_verification_for_trades}
                  onChange={(e) => setSettings(prev => ({ ...prev, require_verification_for_trades: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">Require verification for auto-trade commands</span>
                <input
                  type="checkbox"
                  checked={settings.require_verification_for_auto_trade}
                  onChange={(e) => setSettings(prev => ({ ...prev, require_verification_for_auto_trade: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">Require verification for settings changes</span>
                <input
                  type="checkbox"
                  checked={settings.require_verification_for_settings}
                  onChange={(e) => setSettings(prev => ({ ...prev, require_verification_for_settings: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded"
                />
              </label>
            </div>
            
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Re-enroll / Unenroll Section */}
      {enrollmentStatus?.is_enrolled && (
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-4">Manage Voiceprint</h3>
          <div className="flex gap-4">
            <button
              onClick={() => {
                setRecordings([]);
                setCurrentPhraseIndex(0);
                setEnrollmentStatus(prev => ({ ...prev, is_enrolled: false }));
              }}
              className="flex-1 py-2 px-4 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Re-enroll Voice
            </button>
            <button
              onClick={unenrollVoice}
              className="flex-1 py-2 px-4 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Delete Voiceprint
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Re-enrolling will replace your current voiceprint. Deleting will disable voice verification.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceEnrollment;
